import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { testConditions, testValidators } from 'core/forms';
import { useQueryClient } from '@tanstack/react-query';

import { addErrorsToFormState, getIsCoursePublishing } from '@/exceed';
import { getFileNameFromAwsUrl } from '@/files';

import handleUpdateCoverArt from '../helpers/handleUpdateCoverArt';
import handleUpdateVideoProperties from '../helpers/handleUpdateVideoProperties';

import useVideoCompose from '../hooks/useVideoCompose';
import useVideoProperties from '../hooks/useVideoProperties';
import useVideoVersion from '../hooks/useVideoVersion';

import VideosAdminProperties from '../components/videosAdminProperties';

const VideosAdminPropertiesContainer = () => {
  const { videoComposeKey } = useVideoCompose({ isEnabled: false });
  const { videoVersion, videoVersionKey } = useVideoVersion({ isEnabled: false });
  // eslint-disable-next-line no-unused-vars
  const [formState, setFormState] = useState({});

  const queryClient = useQueryClient();

  const params = {
    params: useParams(),
    queryClient,
    videoComposeKey,
    videoVersionKey,
    versionId: videoVersion.data?.draft_id,
  };

  const { videoProperties, videoPropertiesKey } = useVideoProperties();

  const handleCancel = ({ model, schema }) => {
    setFormState((oldFormState) => {
      Object.entries(schema).forEach(([key, value]) => {
        const field = oldFormState[key];

        if (!field) return;

        const conditionProps = testConditions(key, value, model);
        const errorProps = testValidators(key, value, model);

        Object.assign(field, conditionProps, errorProps, { isDirty: false });
      });

      return oldFormState;
    });
  };

  const updateFormState = (newState) => {
    setFormState((oldFormState) => ({ ...oldFormState, ...newState }));
  };

  const handleUpdate = ({ formState: newFormState }) => {
    updateFormState(newFormState);
  };

  const handleBackendErrors = (error) => {
    addErrorsToFormState({ formState, error });
  };

  const handleSubmit = async ({ modifiedModel, section }) => {
    const clonedModifiedModel = { ...modifiedModel };

    const coverArt = modifiedModel.cover_art
      ? {
          url: modifiedModel.cover_art,
          name: getFileNameFromAwsUrl({ url: modifiedModel.cover_art }),
        }
      : {};

    switch (section) {
      case 'general':
      case 'additional_details':
      case 'catalog':
      case 'public_properties':
      case 'tagging':
      case 'seo_properties':
      case 'enrollment':
        await handleUpdateVideoProperties({
          ...params,
          update: clonedModifiedModel,
          handleBackendErrors,
        });
        break;
      case 'advanced_properties':
      case 'custom_fields':
      case 'social':
        await handleUpdateVideoProperties({
          ...params,
          update: modifiedModel,
          handleBackendErrors,
        });
        break;
      case 'display':
        await handleUpdateCoverArt({
          ...params,
          update: coverArt,
          handleBackendErrors,
        });
        break;
      default:
        return;
    }
  };

  const isFormDisabled = getIsCoursePublishing(videoVersion);

  return (
    <VideosAdminProperties
      isChildLocale={videoVersion.data.is_child_locale}
      isFormDisabled={isFormDisabled}
      videoProperties={videoProperties}
      videoPropertiesKey={videoPropertiesKey}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      onUpdate={handleUpdate}
    />
  );
};

export default VideosAdminPropertiesContainer;
